import { ArrowLeft, ArrowLeftRight, ArrowRight, Bug, Crown, Gauge, Pause, Play, Ticket, Trophy, Users, Volume2, VolumeX, X, Zap } from 'lucide-react';
import { Application, Graphics } from 'pixi.js';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { playerById, teamImageByName, teams, techniqueById } from '../data';
import { ClubBadge } from '../components/ClubBadge';
import { AffinityCrest } from '../components/AffinityCrest';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { TeamBadge } from '../components/TeamBadge';
import { completeLeagueMatchday, createDoubleRoundRobinSchedule, createLeagueGroups, createStandings, findClubFixture, findClubGroup, nextLeagueSeed, playerRating, selectRivalLineup, type RivalTeam } from '../competition';
import { playerStatLabels, rollMatchDevelopment, sourceLabel, type DevelopmentAward, type MatchDevelopmentOutcome } from '../development';
import { resolveClubPlayer, resolveWorldPlayer, rollMatchTokenRewards, type GameState } from '../gameState';
import { useMusic, useMusicScene } from '../music/MusicProvider';
import { recruitmentTokenById } from '../recruitmentTokens';
import { commentatorNarration, matchLogNarration, type MatchNarration } from '../matchNarration';
import { createLiveMatchSession } from '../simulation/matchEngine';
import { leagueDate, nextClubFixture, resolveChampionsTie } from '../careerCalendar';
import { AFFINITY_USER_TEAM_ID, affinityTeams, neutralWinner } from '../specialCompetitions';
import type { ChampionsResult, MatchEvent, MatchResult, Player, PlayerStatKey, SaveGame, Team, WorldFrame } from '../types';

// One real-time minute represents nine match minutes at 1×. The clock moves
// at a practical pace while the engine itself keeps football-scale speeds.
const PRESENTATION_SECONDS = 600;
const MATCH_SECONDS = 5400;
const ACTION_DISPLAY_SECONDS = 12;
const VERSUS_PRESENTATION_MS = 12_800;
const GOAL_CELEBRATION_MS = 1350;
const GOAL_KICKOFF_MS = 720;
const TECHNIQUE_ELEMENT_COLORS: Record<string, string> = {
  FIRE: '#ff6947',
  WIND: '#53cef8',
  FOREST: '#58d596',
  MOUNTAIN: '#f0b24c',
  VOID: '#b495ff',
};

// The match official is also the voice of the broadcast. All four are real
// characters from the imported catalogue, never invented presentation avatars.
const MATCH_OFFICIALS = [
  { playerId: 'inazuma_eleven_api_reference_239', role: 'Árbitro y relator', color: '#58c7ff' }, // Chester Horse Jr.
  { playerId: 'inazuma_eleven_api_reference_244', role: 'Árbitro y relator', color: '#ffb45e' }, // Chester Horse Sr.
  { playerId: 'inazuma_eleven_api_reference_3429', role: 'Árbitro y relator', color: '#b788ff' }, // Gammon Checker
  { playerId: 'inazuma_eleven_api_reference_1981', role: 'Comentarista invitado', color: '#66d4a8' }, // Maxtor Lunde
] as const;

type MatchOfficial = { player: Player; role: string; color: string };

type MatchPhase = 'intro' | 'live' | 'fulltime';
type CompetitionTitle = 'league' | 'champions' | 'kings_cup' | 'affinity_games';
type ScheduledMatch = { groupName: string; matchday: number; date: string; championsRound?: ChampionsResult['round']; competition: 'league' | 'champions' | 'kings_cup' | 'affinity_games'; competitionLabel: string; clubIsHome: boolean; clubTemporary: boolean; clubDisplayName: string; clubPlayers: Player[]; clubBench: Player[]; home: Team; away: Team; seed: number };
type CompletedMatch = { result: MatchResult; match: ScheduledMatch };
type GoalBreak = { key: string; restartTeam: 0 | 1; stage: 'celebration' | 'kickoff' };
type PendingSubstitution = { outgoing: Player; incoming: Player; queuedAtSecond: number; eventCount: number };
type SubstitutionAnimation = { outgoing: Player; incoming: Player };

export function MatchExperiencePage({ game, onExit }: { game: GameState; onExit: () => void }) {
  // The next fixture remains only a calendar entry. It is deliberately not
  // simulated until this screen is opened for that specific match.
  const [match] = useState<ScheduledMatch>(() => createScheduledMatch(game.save));
  const clubSide: 0 | 1 = match.clubIsHome ? 0 : 1;
  const [session] = useState(() => createLiveMatchSession(match.home, match.away, match.seed, clubSide));
  const [liveResult, setLiveResult] = useState<MatchResult>(() => session.getResult());

  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [debugOverlay, setDebugOverlay] = useState(false);
  const [phase, setPhase] = useState<MatchPhase>('intro');
  const [completed, setCompleted] = useState<CompletedMatch | null>(null);
  const [competitionWinner, setCompetitionWinner] = useState<CompetitionTitle | null>(null);
  const [developmentAwards, setDevelopmentAwards] = useState<DevelopmentAward[]>([]);
  const [goalBreak, setGoalBreak] = useState<GoalBreak | null>(null);
  const [substitutionOpen, setSubstitutionOpen] = useState(false);
  const [selectedOutgoingId, setSelectedOutgoingId] = useState<string | null>(null);
  const [selectedIncomingId, setSelectedIncomingId] = useState<string | null>(null);
  const [pendingSubstitution, setPendingSubstitution] = useState<PendingSubstitution | null>(null);
  const [substitutionAnimation, setSubstitutionAnimation] = useState<SubstitutionAnimation | null>(null);
  const [matchLineup, setMatchLineup] = useState<Player[]>(() => match.clubPlayers);
  const [matchBench, setMatchBench] = useState<Player[]>(() => match.clubBench);
  const registered = useRef(false);
  const playedSoundEvents = useRef(new Set<string>());
  const pausedGoalEvents = useRef(new Set<string>());
  const completedGoalBreaks = useRef(new Set<string>());
  const finalWhistlePlayed = useRef(false);
  const resumeAfterChanges = useRef(false);
  const substitutionTimer = useRef<number | null>(null);
  const substitutionExecuting = useRef(false);
  const matchParticipantIds = useRef(new Set(match.clubPlayers.map((player) => player.id)));
  const matchSecond = Math.min(MATCH_SECONDS, elapsed / PRESENTATION_SECONDS * MATCH_SECONDS);
  const atFullTime = matchSecond >= MATCH_SECONDS;
  const result = completed?.result ?? liveResult;
  const shownMatch = completed?.match ?? match;
  const effectiveSecond = phase === 'fulltime' ? MATCH_SECONDS : matchSecond;
  const progress = effectiveSecond / MATCH_SECONDS;
  const visibleEvents = result.events.filter((event) => event.second <= effectiveSecond);
  const latestEvent = visibleEvents.at(-1);
  const currentScore = latestEvent ? [latestEvent.homeScore, latestEvent.awayScore] : [0, 0];
  const activeAction = actionAt(result.events, effectiveSecond);
  const presentationFrame = phase === 'live' ? session.getPresentationFrame() : undefined;
  const commentator = officialForSeed(result.seed);
  const commentatorEvent = visibleEvents.filter((event) => ['GOAL', 'SAVE', 'TECHNIQUE', 'FURY', 'SUBSTITUTION'].includes(event.type)).at(-1);
  const latestGoal = visibleEvents.filter((event) => event.type === 'GOAL').at(-1);
  const latestGoalKey = latestGoal ? `${result.seed}-${latestGoal.second}-${latestGoal.team}-${latestGoal.actorId}` : '';
  const furyActive = phase === 'live' && (presentationFrame ?? frameAt(result.frames, effectiveSecond)).fury.some(Boolean);
  const furyStates = [session.getFuryState(0), session.getFuryState(1)] as const;

  useEffect(() => {
    if (phase !== 'intro') return;
    const timer = window.setTimeout(() => {
      setPhase('live');
      setPlaying(true);
    }, VERSUS_PRESENTATION_MS);
    return () => window.clearTimeout(timer);
  }, [phase, match.seed]);

  useEffect(() => {
    if (phase !== 'live' || !playing || atFullTime) return;
    let previous = performance.now();
    let animationFrame = 0;
    const advancePresentation = (timestamp: number) => {
      // The engine keeps deterministic fixed simulation steps. The broadcast
      // redraw is intentionally decoupled and follows the browser refresh
      // rate, so player steering and long passes are perceived as continuous
      // motion instead of 20 Hz position jumps.
      const elapsedSinceFrame = Math.min(.1, Math.max(0, (timestamp - previous) / 1000));
      const nextElapsed = Math.min(PRESENTATION_SECONDS, elapsedRef.current + elapsedSinceFrame * speed);
      elapsedRef.current = nextElapsed;
      setLiveResult(session.advanceTo(nextElapsed / PRESENTATION_SECONDS * MATCH_SECONDS));
      setElapsed(nextElapsed);
      previous = timestamp;
      if (nextElapsed < PRESENTATION_SECONDS) animationFrame = window.requestAnimationFrame(advancePresentation);
    };
    animationFrame = window.requestAnimationFrame(advancePresentation);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [phase, playing, speed, atFullTime, session]);

  useEffect(() => {
    if (phase !== 'live' || !atFullTime || registered.current) return;
    registered.current = true;
    setPlaying(false);
    const finalResult = session.advanceTo(MATCH_SECONDS);
    setLiveResult(finalResult);
    setCompleted({ result: finalResult, match });
    const clubGoals = match.clubIsHome ? finalResult.homeScore : finalResult.awayScore;
    const rivalGoals = match.clubIsHome ? finalResult.awayScore : finalResult.homeScore;
    const developmentOutcome: MatchDevelopmentOutcome = clubGoals > rivalGoals ? 'win' : clubGoals === rivalGoals ? 'draw' : 'loss';
    const participants = match.clubPlayers.filter((player) => matchParticipantIds.current.has(player.id));
    setDevelopmentAwards(rollMatchDevelopment(participants, finalResult.events, clubSide, developmentOutcome, finalResult.seed).awards);
    const knockoutWinner = match.competition === 'champions'
      ? resolveChampionsTie(finalResult.home.id, finalResult.away.id, finalResult.homeScore, finalResult.awayScore, finalResult.seed).winnerId
      : match.competition === 'kings_cup' || (match.competition === 'affinity_games' && match.competitionLabel.includes('Final'))
        ? neutralWinner(finalResult.home.id, finalResult.away.id, finalResult.homeScore, finalResult.awayScore, finalResult.seed).winnerId
        : undefined;
    const clubTeamId = match.clubTemporary ? AFFINITY_USER_TEAM_ID : 'team_aurora';
    const competitionIsFinal = match.competition === 'champions'
      ? match.championsRound === 'FINAL'
      : match.competition === 'kings_cup' || (match.competition === 'affinity_games' && match.competitionLabel.includes('Final'));
    setCompetitionWinner(match.competition === 'league'
      ? (clubWinsLeagueOnFinalMatch(game.save, match, finalResult) ? 'league' : null)
      : competitionIsFinal && knockoutWinner === clubTeamId ? match.competition : null);
    setPhase('fulltime');
    game.registerResult(finalResult, match.competition, match.date);
  }, [atFullTime, clubSide, game, match, phase, session]);

  useEffect(() => {
    if (phase !== 'live' || atFullTime || !latestGoal || !latestGoalKey || pausedGoalEvents.current.has(latestGoalKey)) return;
    pausedGoalEvents.current.add(latestGoalKey);
    setPlaying(false);
    setGoalBreak({ key: latestGoalKey, restartTeam: latestGoal.team === 0 ? 1 : 0, stage: 'celebration' });
    const kickoffTimer = window.setTimeout(() => setGoalBreak((current) => current?.key === latestGoalKey
      ? { ...current, stage: 'kickoff' }
      : current), GOAL_CELEBRATION_MS);
    const resumeTimer = window.setTimeout(() => {
      completedGoalBreaks.current.add(latestGoalKey);
      setGoalBreak((current) => current?.key === latestGoalKey ? null : current);
      if (!registered.current) setPlaying(true);
    }, GOAL_CELEBRATION_MS + GOAL_KICKOFF_MS);
    return () => {
      window.clearTimeout(kickoffTimer);
      window.clearTimeout(resumeTimer);
    };
  }, [atFullTime, latestGoalKey, phase]);

  const beginMatch = () => {
    setPhase('live');
    setPlaying(true);
  };

  const clubScore = shownMatch.clubIsHome ? result.homeScore : result.awayScore;
  const rivalScore = shownMatch.clubIsHome ? result.awayScore : result.homeScore;
  const outcome = clubScore > rivalScore ? 'victory' : clubScore < rivalScore ? 'defeat' : 'draw';
  const clubFury = session.getFuryState(clubSide);
  const activateFury = () => {
    if (phase !== 'live' || atFullTime || clubFury.used) return;
    if (session.activateFury(clubSide)) setLiveResult(session.getResult());
  };

  const closeSubstitutionPanel = (resume = true) => {
    setSubstitutionOpen(false);
    setSelectedOutgoingId(null);
    setSelectedIncomingId(null);
    if (resume && resumeAfterChanges.current && phase === 'live' && !goalBreak && !atFullTime) setPlaying(true);
    resumeAfterChanges.current = false;
  };

  const openSubstitutionPanel = () => {
    if (phase !== 'live' || atFullTime || goalBreak || substitutionAnimation || pendingSubstitution || !matchBench.length) return;
    resumeAfterChanges.current = playing;
    setPlaying(false);
    setSelectedOutgoingId(null);
    setSelectedIncomingId(null);
    setSubstitutionOpen(true);
  };

  const queueSubstitution = () => {
    const outgoing = matchLineup.find((player) => player.id === selectedOutgoingId);
    const incoming = matchBench.find((player) => player.id === selectedIncomingId);
    if (!outgoing || !incoming) return;
    setPendingSubstitution({ outgoing, incoming, queuedAtSecond: effectiveSecond, eventCount: result.events.length });
    closeSubstitutionPanel(false);
    if (phase === 'live' && !atFullTime && !goalBreak) setPlaying(true);
  };

  const executePendingSubstitution = () => {
    if (!pendingSubstitution || substitutionAnimation || atFullTime || substitutionExecuting.current) return;
    substitutionExecuting.current = true;
    const change = pendingSubstitution;
    setPlaying(false);
    const substituted = session.substitute(clubSide, change.outgoing.id, change.incoming);
    if (!substituted) {
      // A delayed event can make a queued change stale. Clear it and resume
      // safely instead of leaving the match controls locked.
      setPendingSubstitution(null);
      substitutionExecuting.current = false;
      if (!registered.current && phase === 'live' && !atFullTime) setPlaying(true);
      return;
    }
    setPendingSubstitution(null);
    matchParticipantIds.current.add(change.incoming.id);
    setMatchLineup((current) => current.map((player) => player.id === change.outgoing.id ? change.incoming : player));
    // A player who leaves the pitch cannot return in the same match. Only the
    // unused substitutes remain available for later tactical decisions.
    setMatchBench((current) => current.filter((player) => player.id !== change.incoming.id));
    setLiveResult(session.getResult());
    setSubstitutionAnimation({ outgoing: change.outgoing, incoming: change.incoming });
    if (substitutionTimer.current) window.clearTimeout(substitutionTimer.current);
    substitutionTimer.current = window.setTimeout(() => {
      setSubstitutionAnimation(null);
      substitutionTimer.current = null;
      substitutionExecuting.current = false;
      if (!registered.current) setPlaying(true);
    }, 2100);
  };

  useEffect(() => () => {
    if (substitutionTimer.current) window.clearTimeout(substitutionTimer.current);
    substitutionExecuting.current = false;
  }, []);

  useEffect(() => {
    if (!pendingSubstitution || phase !== 'live' || atFullTime || goalBreak || substitutionAnimation) return;
    const goalAfterChange = result.events.slice(pendingSubstitution.eventCount).find((event) => event.type === 'GOAL');
    const fiveMinutesElapsed = effectiveSecond + 0.001 >= pendingSubstitution.queuedAtSecond + 300;
    const goalKey = goalAfterChange ? `${result.seed}-${goalAfterChange.second}-${goalAfterChange.team}-${goalAfterChange.actorId}` : '';
    if (goalAfterChange && !completedGoalBreaks.current.has(goalKey)) return;
    if (goalAfterChange || fiveMinutesElapsed) executePendingSubstitution();
  }, [atFullTime, effectiveSecond, goalBreak, pendingSubstitution, phase, result.events, substitutionAnimation]);
  const { enabled: musicEnabled, toggle: toggleMusic, setFuryActive: setFuryMusicActive, playEffect } = useMusic();
  useMusicScene(phase === 'intro' ? 'versus' : phase === 'live' ? 'match' : outcome);

  useEffect(() => {
    setFuryMusicActive(furyActive);
    return () => setFuryMusicActive(false);
  }, [furyActive, setFuryMusicActive]);

  useEffect(() => {
    if (phase !== 'live' || atFullTime || !latestGoal) return;
    const key = `${result.seed}-${latestGoal.second}-${latestGoal.team}-${latestGoal.actorId}`;
    if (playedSoundEvents.current.has(key)) return;
    playedSoundEvents.current.add(key);
    playEffect('goal');
  }, [atFullTime, latestGoal, phase, playEffect, result.seed]);

  useEffect(() => {
    if (phase !== 'fulltime' || finalWhistlePlayed.current) return;
    finalWhistlePlayed.current = true;
    playEffect('finalWhistle');
  }, [phase, playEffect]);

  const queuedChangeCountdown = pendingSubstitution ? Math.max(0, pendingSubstitution.queuedAtSecond + 300 - effectiveSecond) : 0;

  return <div className={`match-page match-phase-${phase} ${goalBreak ? 'match-goal-break' : ''}`}>
    <header className="match-header">
      <button className="match-back" onClick={onExit}><ArrowLeft size={18} /> Salir del partido</button>
      <div className="match-competition"><span>LIGA · {shownMatch.groupName.toUpperCase()}</span><small>Jornada {shownMatch.matchday} · Condiciones perfectas</small></div>
      <button className={`sound-button ${musicEnabled ? '' : 'muted'}`} onClick={toggleMusic} aria-label={musicEnabled ? 'Silenciar música' : 'Activar música'} title={musicEnabled ? 'Silenciar música' : 'Activar música'}>{musicEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}</button>
    </header>

    {phase !== 'fulltime' && <Scoreboard game={game} match={shownMatch} result={result} minute={effectiveSecond} phase={phase} score={currentScore} />}
    {phase !== 'fulltime' && <CommentaryPopup commentator={commentator} event={commentatorEvent} matchSeed={result.seed} />}

    {phase === 'fulltime' && completed ? <main className="match-workspace match-workspace--fulltime">
        <FulltimeOverlay game={game} completed={completed} outcome={outcome} developmentAwards={developmentAwards} competitionWinner={competitionWinner} onExit={onExit} />
    </main> : <main className="match-workspace">
      <section className="pitch-shell">
        <PixiPitch result={result} matchSecond={effectiveSecond} presentationFrame={presentationFrame} debugEnabled={debugOverlay} />
        {phase === 'live' && <FuryTimers states={furyStates} teams={[result.home.name, result.away.name]} />}
        <PitchPlayerOverlay result={result} matchSecond={effectiveSecond} presentationFrame={presentationFrame} action={activeAction} />
        <PitchBallOverlay result={result} matchSecond={effectiveSecond} presentationFrame={presentationFrame} action={activeAction} />
        {debugOverlay && <AIDebugOverlay result={result} matchSecond={effectiveSecond} presentationFrame={presentationFrame} />}
        <GoalCelebration event={latestGoal} active={goalBreak?.stage === 'celebration'} />
        {goalBreak?.stage === 'kickoff' && <KickoffRestart team={goalBreak.restartTeam === 0 ? result.home : result.away} />}
        {substitutionAnimation && <SubstitutionAnimationOverlay outgoing={substitutionAnimation.outgoing} incoming={substitutionAnimation.incoming} />}
        {phase === 'intro' && <VersusTransition game={game} match={match} onStart={beginMatch} />}
      </section>

      <aside className="match-sidebar">
        <div className="live-label"><i /> EN DIRECTO <span>Seed {result.seed}</span></div>
        <div className="event-feed" aria-label="Cronología del partido">
          {visibleEvents.slice(-8).reverse().map((event, index) => <div className={`feed-event ${index === 0 ? 'latest' : ''}`} key={`${event.second}-${event.type}-${index}`}><time>{formatClock(event.second)}</time><span><div className="feed-message"><EventMessage event={event} /></div><small>{event.type}</small></span></div>)}
          {!visibleEvents.length && <div className="feed-empty">La alineación ya está sobre el césped.</div>}
        </div>
        <div className="live-stats"><span className="eyebrow">DATOS EN VIVO</span><LiveStat label="Posesión" home={result.stats.possession[0]} away={result.stats.possession[1]} suffix="%" /><LiveStat label="Tiros" home={result.stats.shots[0]} away={result.stats.shots[1]} /><LiveStat label="A puerta" home={result.stats.shotsOnTarget[0]} away={result.stats.shotsOnTarget[1]} /><LiveStat label="Pases" home={result.stats.passes[0]} away={result.stats.passes[1]} /></div>
      </aside>
    </main>}

    {phase !== 'fulltime' && <footer className="match-controls">
      <button onClick={() => setPlaying((value) => !value)} disabled={phase !== 'live' || Boolean(goalBreak) || substitutionOpen || Boolean(substitutionAnimation)} className="play-control">{playing ? <Pause size={18} /> : <Play size={18} />}</button>
      <span className="control-time">{formatClock(effectiveSecond)}</span>
      <div className="timeline-track live-locked" title="El partido se simula en directo; usa las velocidades para avanzar"><i style={{ width: `${progress * 100}%` }} /><b style={{ left: `${progress * 100}%` }} /></div>
      <span className="control-time dim">90:00</span>
      <button className={`substitution-control ${pendingSubstitution ? 'queued' : ''}`} onClick={openSubstitutionPanel} disabled={phase !== 'live' || atFullTime || Boolean(goalBreak) || Boolean(substitutionAnimation) || !matchBench.length || Boolean(pendingSubstitution)} title={pendingSubstitution ? `Cambio preparado: se ejecutará con el próximo gol o en ${formatClock(queuedChangeCountdown)}` : matchBench.length ? 'Gestionar cambios con el banquillo' : 'No quedan suplentes disponibles'}>
        <ArrowLeftRight size={16} /> {pendingSubstitution ? `CAMBIO ${formatClock(queuedChangeCountdown)}` : 'CAMBIOS'}
      </button>
      <button className={`fury-control ${clubFury.active ? 'active' : ''}`} onClick={activateFury} disabled={phase !== 'live' || atFullTime || clubFury.used || Boolean(goalBreak) || substitutionOpen || Boolean(substitutionAnimation)} title={clubFury.used ? 'La única Fase de Furor del equipo ya se ha utilizado' : 'Activa tres minutos de bonificación para tu equipo'}>
        <Zap size={16} /> {clubFury.active ? `FUROR ${formatClock(clubFury.remainingSeconds)}` : clubFury.used ? 'FUROR USADO' : 'ACTIVAR FUROR'}
      </button>
      <button className={`ai-debug-control ${debugOverlay ? 'active' : ''}`} onClick={() => setDebugOverlay((value) => !value)} title="Mostrar anchors, targets, intención, percepción y scores de la IA" aria-pressed={debugOverlay}>
        <Bug size={15} /> IA
      </button>
      <div className="speed-control"><Gauge size={16} />{[1, 2, 4].map((value) => <button disabled={phase !== 'live' || Boolean(goalBreak) || substitutionOpen || Boolean(substitutionAnimation)} className={speed === value ? 'active' : ''} onClick={() => setSpeed(value)} key={value}>{value}×</button>)}</div>
    </footer>}
    {substitutionOpen && <SubstitutionPanel
      lineup={matchLineup}
      bench={matchBench}
      getLiveTp={(player) => session.getPlayerTp(player.id) ?? { current: player.tp_max, max: player.tp_max }}
      selectedOutgoingId={selectedOutgoingId}
      selectedIncomingId={selectedIncomingId}
      onSelectOutgoing={setSelectedOutgoingId}
      onSelectIncoming={setSelectedIncomingId}
      onClose={() => closeSubstitutionPanel()}
      onConfirm={queueSubstitution}
    />}
  </div>;
}

function SubstitutionPanel({
  lineup,
  bench,
  getLiveTp,
  selectedOutgoingId,
  selectedIncomingId,
  onSelectOutgoing,
  onSelectIncoming,
  onClose,
  onConfirm,
}: {
  lineup: Player[];
  bench: Player[];
  getLiveTp: (player: Player) => { current: number; max: number };
  selectedOutgoingId: string | null;
  selectedIncomingId: string | null;
  onSelectOutgoing: (id: string) => void;
  onSelectIncoming: (id: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const outgoing = lineup.find((player) => player.id === selectedOutgoingId);
  const incoming = bench.find((player) => player.id === selectedIncomingId);
  return <div className="match-substitution-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="match-substitution-panel" role="dialog" aria-modal="true" aria-labelledby="match-substitution-title" onMouseDown={(event) => event.stopPropagation()}>
      <header>
        <div><span className="eyebrow">DECISIÓN TÁCTICA</span><h2 id="match-substitution-title">Cambios de jugadores</h2><p>El partido está en pausa. Elige quién deja el once y el suplente que ocupará su puesto.</p></div>
        <button onClick={onClose} aria-label="Cerrar cambios"><X size={19} /></button>
      </header>
      <div className="match-substitution-layout">
        <section className="match-substitution-roster">
          <div className="match-substitution-section-title"><span>ONCE TITULAR</span><small>{lineup.length} jugadores</small></div>
          <div className="match-substitution-list">
            {lineup.map((player, index) => <button className={selectedOutgoingId === player.id ? 'active' : ''} onClick={() => onSelectOutgoing(player.id)} key={player.id}>
              <PlayerAvatar player={player} size="sm" />
              <span><small>PUESTO {index + 1} · {player.position}</small><b>{player.name}</b></span>
              <i>SALE</i>
            </button>)}
          </div>
        </section>
        <section className="match-substitution-comparison" aria-label="Comparativa del cambio">
          <span className="match-substitution-label is-out">SALE DEL ONCE</span>
          {outgoing ? <MatchChangeProfile player={outgoing} direction="out" tp={getLiveTp(outgoing)} /> : <MatchChangePlaceholder direction="out" />}
          <div className="match-substitution-action"><ArrowLeftRight size={23} /><span>INTERCAMBIO</span><button disabled={!outgoing || !incoming} onClick={onConfirm}>{outgoing && incoming ? 'Preparar cambio' : 'Elige dos jugadores'}</button></div>
          <span className="match-substitution-label is-in">ENTRA DESDE EL BANQUILLO</span>
          {incoming ? <MatchChangeProfile player={incoming} direction="in" tp={getLiveTp(incoming)} compareWith={outgoing} /> : <MatchChangePlaceholder direction="in" />}
        </section>
        <section className="match-substitution-roster bench">
          <div className="match-substitution-section-title"><span>BANQUILLO</span><small>{bench.length} disponibles</small></div>
          <div className="match-substitution-list">
            {bench.map((player) => <button className={selectedIncomingId === player.id ? 'active' : ''} onClick={() => onSelectIncoming(player.id)} key={player.id}>
              <PlayerAvatar player={player} size="sm" />
              <span><small>{player.position} · {player.element}</small><b>{player.name}</b></span>
              <i>ENTRA</i>
            </button>)}
          </div>
        </section>
      </div>
      <footer><span><Zap size={15} /> El cambio se realizará tras el próximo gol o dentro de cinco minutos de juego.</span><button onClick={onClose}>Cancelar</button></footer>
    </section>
  </div>;
}

function MatchChangeProfile({ player, direction, tp, compareWith }: { player: Player; direction: 'in' | 'out'; tp: { current: number; max: number }; compareWith?: Player }) {
  const stats = Object.entries(player.stats) as [PlayerStatKey, number][];
  const currentTp = Math.max(0, Math.min(tp.max, Math.round(tp.current)));
  const tpPercent = tp.max > 0 ? currentTp / tp.max * 100 : 0;
  return <article className={`match-change-profile match-change-profile--${direction}`}>
    <div className="match-change-profile-identity"><PlayerAvatar player={player} size="md" /><span><small>{player.position} · MEDIA {playerAverage(player)}</small><b>{player.name}</b><em>{player.metadata?.team ?? 'Plantilla'}</em></span></div>
    <div className="match-change-profile-stats">{stats.map(([stat, value]) => <span key={stat}><small>{playerStatLabels[stat] ?? stat}</small><b>{value}</b>{compareWith && <em className={value >= (compareWith.stats[stat] ?? 0) ? 'positive' : 'negative'}>{value >= (compareWith.stats[stat] ?? 0) ? '+' : ''}{value - (compareWith.stats[stat] ?? 0)}</em>}</span>)}</div>
    <footer>
      <span>{player.element}</span>
      <strong className="match-change-tp" title="TP actual en el partido"><small>TP ACTUAL</small><b>{currentTp}/{tp.max}</b><i><em style={{ width: `${tpPercent}%` }} /></i></strong>
      <small>{player.techniques.length} supertécnicas</small>
    </footer>
  </article>;
}

function MatchChangePlaceholder({ direction }: { direction: 'in' | 'out' }) {
  const outgoing = direction === 'out';
  return <div className={`match-change-placeholder ${outgoing ? 'is-out' : ''}`}><Users size={21} /><b>{outgoing ? 'Selecciona un titular' : 'Selecciona un suplente'}</b><small>{outgoing ? 'Verás aquí el jugador que abandona el campo.' : 'Compara al revulsivo con el jugador que saldrá.'}</small></div>;
}

function SubstitutionAnimationOverlay({ outgoing, incoming }: { outgoing: Player; incoming: Player }) {
  return <div className="match-substitution-animation" role="status" aria-live="assertive">
    <span className="match-substitution-animation-kicker"><ArrowLeftRight size={15} /> CAMBIO CONFIRMADO</span>
    <div>
      <article className="is-out"><span>SALE</span><PlayerAvatar player={outgoing} size="md" /><b>{playerAlias(outgoing.id)}</b></article>
      <i><ArrowLeftRight size={28} /></i>
      <article className="is-in"><span>ENTRA</span><PlayerAvatar player={incoming} size="md" /><b>{playerAlias(incoming.id)}</b></article>
    </div>
    <p><strong>{playerAlias(outgoing.id)}</strong> deja su sitio a <strong>{playerAlias(incoming.id)}</strong>.</p>
  </div>;
}

function Scoreboard({ game, match, result, minute, phase, score }: { game: GameState; match: ScheduledMatch; result: MatchResult; minute: number; phase: MatchPhase; score: number[] }) {
  const homeScore = phase === 'fulltime' ? result.homeScore : score[0];
  const awayScore = phase === 'fulltime' ? result.awayScore : score[1];
  const period = phase === 'fulltime' ? 'FINAL' : minute >= 2700 ? 'SEGUNDA PARTE' : 'PRIMERA PARTE';
  return <div className="scoreboard">
    <div className="score-team home">
      <div className="score-team-copy"><span>LOCAL</span><b>{result.home.name}</b></div>
      <div className="score-team-badge"><MatchTeamBadge match={match} save={game.save} team={result.home} isClub={match.clubIsHome} size="md" /></div>
    </div>
    <div className="score-center">
      <span className="score-minute">{phase === 'intro' ? 'PREPARTIDO' : formatMinute(minute)}</span>
      <strong><em>{homeScore}</em><i>—</i><em>{awayScore}</em></strong>
      <small>{period}</small>
    </div>
    <div className="score-team away">
      <div className="score-team-badge"><MatchTeamBadge match={match} save={game.save} team={result.away} isClub={!match.clubIsHome} size="md" /></div>
      <div className="score-team-copy"><span>VISITANTE</span><b>{result.away.name}</b></div>
    </div>
  </div>;
}

function MatchClubBadge({ match, save, size }: { match: ScheduledMatch; save: SaveGame; size: 'sm' | 'md' | 'lg' }) {
  return match.clubTemporary && save.affinityGames
    ? <AffinityCrest element={save.affinityGames.element} size="hero" />
    : <ClubBadge save={save} size={size} />;
}

function MatchTeamBadge({ match, save, team, isClub, size }: { match: ScheduledMatch; save: SaveGame; team: Team; isClub: boolean; size: 'sm' | 'md' | 'lg' }) {
  if (isClub) return <MatchClubBadge match={match} save={save} size={size} />;
  const affinityElement = match.competition === 'affinity_games'
    ? Object.values(team.playerOverrides ?? {})[0]?.element
    : undefined;
  return affinityElement
    ? <AffinityCrest element={affinityElement} size="hero" />
    : <TeamBadge name={team.name} imageUrl={teamImageByName.get(team.name.toLocaleLowerCase())} size={size} />;
}

function VersusTransition({ game, match, onStart }: { game: GameState; match: ScheduledMatch; onStart: () => void }) {
  const rival = match.clubIsHome ? match.away : match.home;
  return <div className="versus-transition">
    <div className="versus-transition-rays" aria-hidden="true"><i /><i /><i /></div>
    <span className="versus-kicker"><Zap size={15} /> {match.competition === 'league' ? `JORNADA ${match.matchday} · ` : ''}{match.competitionLabel.toUpperCase()}</span>
    <div className="versus-teams">
      <div><MatchTeamBadge match={match} save={game.save} team={match.clubIsHome ? match.home : match.away} isClub size="lg" /><b>{match.clubDisplayName}</b><small>{match.competition === 'kings_cup' || match.competition === 'affinity_games' ? 'NEUTRAL' : match.clubIsHome ? 'LOCAL' : 'VISITANTE'}</small></div>
      <strong>VS</strong>
      <div><MatchTeamBadge match={match} save={game.save} team={rival} isClub={false} size="lg" /><b>{rival.name}</b><small>{match.clubIsHome ? 'VISITANTE' : 'LOCAL'}</small></div>
    </div>
    <button onClick={onStart}>Saltar presentación <ArrowRight size={17} /></button>
  </div>;
}

function FulltimeOverlay({ game, completed, outcome, developmentAwards, competitionWinner, onExit }: { game: GameState; completed: CompletedMatch; outcome: 'victory' | 'defeat' | 'draw'; developmentAwards: DevelopmentAward[]; competitionWinner: CompetitionTitle | null; onExit: () => void }) {
  const clubScore = completed.match.clubIsHome ? completed.result.homeScore : completed.result.awayScore;
  const rivalScore = completed.match.clubIsHome ? completed.result.awayScore : completed.result.homeScore;
  const isKnockout = completed.match.competition === 'champions' || completed.match.competition === 'kings_cup' || (completed.match.competition === 'affinity_games' && completed.match.competitionLabel.includes('Final'));
  const shootout = isKnockout && clubScore === rivalScore
    ? completed.match.competition === 'champions'
      ? resolveChampionsTie(completed.result.home.id, completed.result.away.id, completed.result.homeScore, completed.result.awayScore, completed.result.seed)
      : neutralWinner(completed.result.home.id, completed.result.away.id, completed.result.homeScore, completed.result.awayScore, completed.result.seed)
    : null;
  const clubWonShootout = shootout?.winnerId === (completed.match.clubTemporary ? AFFINITY_USER_TEAM_ID : 'team_aurora');
  const effectiveOutcome = shootout ? clubWonShootout ? 'victory' : 'defeat' : outcome;
  const shootoutClubScore = shootout ? (completed.match.clubIsHome ? shootout.penaltyHomeScore : shootout.penaltyAwayScore) : undefined;
  const shootoutRivalScore = shootout ? (completed.match.clubIsHome ? shootout.penaltyAwayScore : shootout.penaltyHomeScore) : undefined;
  const championsAdvance = completed.match.competition === 'champions' && completed.match.championsRound !== 'FINAL' && effectiveOutcome === 'victory';
  const title = shootout ? clubWonShootout ? 'Clasificación desde los once metros' : 'La tanda decide el camino' : championsAdvance ? '¡A la siguiente ronda!' : effectiveOutcome === 'victory' ? 'Victoria con carácter' : effectiveOutcome === 'defeat' ? 'El equipo volverá más fuerte' : 'Un punto que suma';
  const copy = shootout ? clubWonShootout ? 'La tanda de penaltis te abre la siguiente ronda.' : 'La eliminatoria se decide en la tanda de penaltis.' : championsAdvance ? 'El equipo supera la eliminatoria y sigue vivo en Champions.' : effectiveOutcome === 'victory' ? 'Tu vestuario celebra una gran actuación.' : effectiveOutcome === 'defeat' ? 'Analiza el partido y prepara la respuesta.' : 'La batalla queda abierta para la próxima jornada.';
  const next = nextMatchPreview(game.save, completed.match.matchday);
  const clubPossession = completed.match.clubIsHome ? completed.result.stats.possession[0] : completed.result.stats.possession[1];
  const rivalPossession = completed.match.clubIsHome ? completed.result.stats.possession[1] : completed.result.stats.possession[0];
  const clubShots = completed.match.clubIsHome ? completed.result.stats.shots[0] : completed.result.stats.shots[1];
  const rivalShots = completed.match.clubIsHome ? completed.result.stats.shots[1] : completed.result.stats.shots[0];
  const clubOnTarget = completed.match.clubIsHome ? completed.result.stats.shotsOnTarget[0] : completed.result.stats.shotsOnTarget[1];
  const rivalOnTarget = completed.match.clubIsHome ? completed.result.stats.shotsOnTarget[1] : completed.result.stats.shotsOnTarget[0];
  const clubPasses = completed.match.clubIsHome ? completed.result.stats.passes[0] : completed.result.stats.passes[1];
  const rivalPasses = completed.match.clubIsHome ? completed.result.stats.passes[1] : completed.result.stats.passes[0];
  const rival = completed.match.clubIsHome ? completed.result.away : completed.result.home;
  const mvp = pickMatchMvp(game, completed.result);
  const highlights = selectMatchHighlights(completed.result.events);
  const statistics = [
    { label: 'Posesión', club: clubPossession, rival: rivalPossession, suffix: '%' },
    { label: 'Tiros', club: clubShots, rival: rivalShots, suffix: '' },
    { label: 'A puerta', club: clubOnTarget, rival: rivalOnTarget, suffix: '' },
    { label: 'Pases', club: clubPasses, rival: rivalPasses, suffix: '' },
  ];
  const rewards = rollMatchTokenRewards(completed.result.seed, effectiveOutcome === 'victory' ? 'win' : effectiveOutcome === 'draw' ? 'draw' : 'loss');
  return <div className={`fulltime-overlay fulltime-${effectiveOutcome} ${competitionWinner ? `fulltime-champion fulltime-champion--${competitionWinner}` : ''}`}>
    <div className="fulltime-flare" aria-hidden="true" />
    <div className="fulltime-confetti" aria-hidden="true"><i /><i /><i /><i /><i /></div>
    <section className="fulltime-card">
      <header className="fulltime-report-hero">
        <div className="fulltime-result-copy">{competitionWinner && <CompetitionWinnerAnnouncement competition={competitionWinner} clubName={game.save.clubName} />}<span className={`fulltime-stamp fulltime-stamp--${effectiveOutcome}`} aria-hidden="true">{effectiveOutcome === 'victory' ? 'VICTORIA' : effectiveOutcome === 'defeat' ? 'DERROTA' : 'EMPATE'}</span><span className="fulltime-kicker"><Trophy size={15} /> INFORME FINAL · JORNADA {completed.match.matchday}</span><h2>{title}</h2><p>{copy}</p></div>
        <div className="fulltime-scoreboard">
          <div className="fulltime-team fulltime-team--club"><MatchTeamBadge match={completed.match} save={game.save} team={completed.match.clubIsHome ? completed.match.home : completed.match.away} isClub size="md" /><b>{completed.match.clubDisplayName}</b><small>{completed.match.competition === 'kings_cup' || completed.match.competition === 'affinity_games' ? 'NEUTRAL' : completed.match.clubIsHome ? 'LOCAL' : 'VISITANTE'}</small></div>
          <div className="fulltime-score"><strong>{clubScore}<i>—</i>{rivalScore}</strong><small>{shootout ? `PEN. ${shootoutClubScore} — ${shootoutRivalScore}` : 'RESULTADO FINAL'}</small></div>
          <div className="fulltime-team"><MatchTeamBadge match={completed.match} save={game.save} team={rival} isClub={false} size="md" /><b>{rival.name}</b><small>{completed.match.clubIsHome ? 'VISITANTE' : 'LOCAL'}</small></div>
        </div>
      </header>

      <div className="fulltime-report-grid">
        <section className="fulltime-analysis-panel">
          <header className="fulltime-section-heading"><span>RADIOGRAFÍA DEL PARTIDO</span><small>{game.save.clubName} <i>vs</i> {rival.name}</small></header>
          <div className="fulltime-stat-table" aria-label="Estadísticas comparativas del partido">
            {statistics.map((stat) => {
              const total = stat.club + stat.rival || 1;
              return <article key={stat.label}><b>{stat.club}{stat.suffix}</b><span><small>{stat.label}</small><div><i style={{ width: `${stat.club / total * 100}%` }} /><em style={{ width: `${stat.rival / total * 100}%` }} /></div></span><b>{stat.rival}{stat.suffix}</b></article>;
            })}
          </div>
          <section className="fulltime-highlights" aria-label="Momentos destacados">
            <header className="fulltime-section-heading"><span>MOMENTOS DESTACADOS</span><small>{highlights.length ? `${highlights.length} jugadas clave` : 'Partido sin acciones destacadas'}</small></header>
            <div>{highlights.length ? highlights.map((event, index) => <article className={`fulltime-highlight fulltime-highlight--${event.type.toLowerCase()}`} key={`${event.second}-${event.type}-${event.actorId}-${index}`}><time>{formatMinute(event.second)}</time><span><small>{highlightLabel(event.type)}</small><b><EventMessage event={event} /></b></span></article>) : <p>No hubo acciones de impacto registradas.</p>}</div>
          </section>
        </section>

        <aside className="fulltime-report-side">
          <section className="fulltime-mvp" aria-label="MVP del partido">
            <span><Trophy size={14} /> MVP DEL PARTIDO</span>
            {mvp ? <div><PlayerAvatar player={mvp.player} size="lg" /><span><small>{mvp.teamName.toUpperCase()}</small><b>{mvp.player.metadata?.nickname ?? mvp.player.name}</b><em>{mvp.summary}</em></span></div> : <p>La figura del partido se determinará al cerrar el acta.</p>}
          </section>
          <section className="fulltime-rewards" aria-label="Recompensas obtenidas"><span><Ticket size={14} /> RECOMPENSAS OBTENIDAS</span><div>{Object.entries(rewards).map(([tokenId, quantity]) => { const token = recruitmentTokenById.get(tokenId); const TokenIcon = token?.icon ?? Ticket; return <b className={`token-${token?.color ?? 'youth'}`} key={tokenId}><TokenIcon size={14} />{token?.name ?? tokenId} <em>×{quantity}</em></b>; })}</div></section>
          {developmentAwards.length > 0 && <section className="fulltime-development" aria-label="Mejoras de jugadores"><span>PROGRESIÓN DEL PARTIDO</span><div>{developmentAwards.map((award, index) => {
            const player = game.ownedPlayers.find((candidate) => candidate.id === award.playerId) ?? playerById.get(award.playerId);
            return player && <article key={`${award.playerId}-${award.stat}-${award.source}-${index}`}><PlayerAvatar player={player} size="sm" /><span><b>{player.metadata?.nickname ?? player.name}</b><small>{sourceLabel(award.source)}</small></span><strong>+1 <em>{playerStatLabels[award.stat]}</em></strong></article>;
          })}</div></section>}
        </aside>
      </div>

      <footer className="fulltime-report-footer">
        {next && <section className="next-match-reveal">
          <span>PRÓXIMO PARTIDO · TEMPORADA {next.season} · JORNADA {next.matchday}</span>
          <div>
            {next.clubIsHome ? <ClubBadge save={game.save} size="sm" /> : <TeamBadge name={next.opponent.name} imageUrl={next.opponent.image_url} size="sm" />}
            <b>{next.clubIsHome ? game.save.clubName : next.opponent.name}</b><em>VS</em><b>{next.clubIsHome ? next.opponent.name : game.save.clubName}</b>
            {next.clubIsHome ? <TeamBadge name={next.opponent.name} imageUrl={next.opponent.image_url} size="sm" /> : <ClubBadge save={game.save} size="sm" />}
          </div>
          <small>Rival confirmado · resultado pendiente de jugarse</small>
        </section>}
        <div className="fulltime-actions"><button className="primary-action" onClick={onExit}>Continuar al club <ArrowRight size={16} /></button></div>
      </footer>
    </section>
  </div>;
}

function CompetitionWinnerAnnouncement({ competition, clubName }: { competition: CompetitionTitle; clubName: string }) {
  const copy = {
    league: { title: '¡CAMPEONES DE LIGA!', trophy: 'el Trofeo Relámpago' },
    champions: { title: '¡CAMPEONES DE CHAMPIONS!', trophy: 'la Copa Champions' },
    kings_cup: { title: '¡CAMPEONES DE LA KING’S CUP!', trophy: 'la King’s Cup' },
    affinity_games: { title: '¡CAMPEONES DE LOS AFFINITY GAMES!', trophy: 'el trofeo de los Affinity Games' },
  }[competition];
  return <div className={`competition-winner-announcement competition-winner-announcement--${competition}`}>
    <span className="competition-winner-logo">{competition === 'kings_cup' ? <Crown size={31} aria-hidden="true" /> : competition === 'affinity_games' ? <img src="/affinity-games-trophy.png" alt="" /> : <img src="/inazuma-competition-logos.png" alt="" />}</span>
    <span><small>CAMPEONATO CONQUISTADO</small><b>{copy.title}</b><em>{clubName} levanta {copy.trophy}.</em></span>
  </div>;
}

function pickMatchMvp(game: GameState, result: MatchResult): { player: Player; teamName: string; summary: string } | null {
  const candidates = new Map<string, { id: string; team: 0 | 1; impact: number; goals: number; saves: number; moments: number }>();
  const scoreFor = (type: MatchEvent['type']) => ({
    GOAL: 12, SAVE: 7, REBOUND: 5, DEFLECTION: 2, TECHNIQUE: 3, SHOT: 2, TACKLE: 2,
    DRIBBLE: 1.5, PASS: .12, FURY: 0, OUT: 0, THROW_IN: 0, CORNER: 0, GOAL_KICK: 0,
    OFFSIDE: 0, FOUL: -0.5, SUBSTITUTION: 0,
  }[type]);
  result.events.forEach((event) => {
    const key = `${event.team}:${event.actorId}`;
    const previous = candidates.get(key) ?? { id: event.actorId, team: event.team, impact: 0, goals: 0, saves: 0, moments: 0 };
    const impact = scoreFor(event.type);
    candidates.set(key, {
      ...previous,
      impact: previous.impact + impact,
      goals: previous.goals + (event.type === 'GOAL' ? 1 : 0),
      saves: previous.saves + (event.type === 'SAVE' || event.type === 'REBOUND' ? 1 : 0),
      moments: previous.moments + (impact >= 1 ? 1 : 0),
    });
  });
  const candidate = [...candidates.values()].sort((left, right) => right.impact - left.impact || right.goals - left.goals || right.saves - left.saves || left.id.localeCompare(right.id))[0];
  if (!candidate) return null;
  const team = candidate.team === 0 ? result.home : result.away;
  const player = team.playerOverrides?.[candidate.id] ?? game.ownedPlayers.find((item) => item.id === candidate.id) ?? playerById.get(candidate.id);
  if (!player) return null;
  const summary = candidate.goals ? `${candidate.goals} ${candidate.goals === 1 ? 'gol decisivo' : 'goles decisivos'}`
    : candidate.saves ? `${candidate.saves} ${candidate.saves === 1 ? 'parada clave' : 'paradas clave'}`
      : `${Math.max(1, candidate.moments)} acciones de impacto`;
  return { player, teamName: team.name, summary };
}

function selectMatchHighlights(events: MatchEvent[]): MatchEvent[] {
  const weight: Record<MatchEvent['type'], number> = {
    GOAL: 100, SAVE: 65, REBOUND: 58, TECHNIQUE: 48, SHOT: 32, DEFLECTION: 24, TACKLE: 22,
    DRIBBLE: 15, FOUL: 12, OFFSIDE: 8, PASS: 0, FURY: 0, OUT: 0, THROW_IN: 0, CORNER: 0, GOAL_KICK: 0, SUBSTITUTION: 0,
  };
  const seen = new Set<string>();
  return [...events]
    .sort((left, right) => weight[right.type] - weight[left.type] || right.second - left.second)
    .filter((event) => {
      if (!weight[event.type]) return false;
      const key = `${event.team}:${event.actorId}:${event.type}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 4)
    .sort((left, right) => left.second - right.second);
}

function highlightLabel(type: MatchEvent['type']) {
  const labels: Record<MatchEvent['type'], string> = {
    GOAL: 'GOL', SAVE: 'PARADA', TECHNIQUE: 'SUPERTÉCNICA', SHOT: 'DISPARO', TACKLE: 'RECUPERACIÓN', DRIBBLE: 'REGATE',
    PASS: 'PASE', DEFLECTION: 'DESVÍO', REBOUND: 'RECHACE', FURY: 'FUROR', OUT: 'FUERA',
    THROW_IN: 'SAQUE DE BANDA', CORNER: 'CÓRNER', GOAL_KICK: 'SAQUE DE PUERTA', OFFSIDE: 'FUERA DE JUEGO',
    FOUL: 'FALTA', SUBSTITUTION: 'CAMBIO',
  };
  return labels[type];
}

function PitchPlayerOverlay({ result, matchSecond, presentationFrame, action }: { result: MatchResult; matchSecond: number; presentationFrame?: WorldFrame; action?: MatchEvent }) {
  const frame = presentationFrame ?? frameAt(result.frames, matchSecond);
  return <div className="match-player-layer" aria-label="Jugadores en el campo">
    {frame.players.map((worldPlayer, index) => {
      const player = playerById.get(worldPlayer.id);
      if (!player) return null;
      const actionType = action?.actorId === worldPlayer.id && action.team === worldPlayer.team ? action.type.toLowerCase() : '';
      const technique = actionType && action?.techniqueId ? techniqueById.get(action.techniqueId) : undefined;
      const goalkeeperTechniqueSave = actionType === 'save' && technique?.type === 'SAVE';
      const techniqueActivation = actionType === 'technique';
      const fieldTechnique = technique?.type === 'SHOT' && (techniqueActivation || ['shot', 'goal', 'out'].includes(actionType))
        ? 'shot'
        : technique?.type === 'BLOCK' && (techniqueActivation || actionType === 'tackle')
          ? 'block'
          : technique?.type === 'DRIBBLE' && (techniqueActivation || actionType === 'dribble')
            ? 'dribble'
            : undefined;
      const techniqueColor = technique ? TECHNIQUE_ELEMENT_COLORS[technique.element] ?? TECHNIQUE_ELEMENT_COLORS.VOID : undefined;
      // A copied player may be present for both sides. Team plus slot keeps
      // React's identity unique even when the catalog identity is the same.
      return <div className={`match-pitch-player team-${worldPlayer.team} element-ring--${player.element.toLowerCase()} ${frame.fury[worldPlayer.team] ? 'is-fury' : ''} ${actionType ? `is-action action-${actionType}` : ''} ${goalkeeperTechniqueSave ? 'is-goalkeeper-technique-save' : ''} ${fieldTechnique ? `is-field-technique-${fieldTechnique}` : ''}`} style={{ left: `${worldPlayer.x}%`, top: `${worldPlayer.y}%`, ...(techniqueColor ? { '--technique-color': techniqueColor } : {}) } as CSSProperties} title={`${player.name} · ${worldPlayer.role}`} key={`${worldPlayer.team}-${worldPlayer.id}-${index}`}>
        <PlayerAvatar player={player} size="sm" />
        {goalkeeperTechniqueSave && <GoalkeeperTechniqueSaveEffect />}
        {fieldTechnique && <FieldTechniqueEffect key={`${fieldTechnique}-${action?.second}-${action?.actorId}-${action?.techniqueId}`} type={fieldTechnique} />}
        <span>{playerAlias(worldPlayer.id)}</span>
      </div>;
    })}
  </div>;
}

function GoalkeeperTechniqueSaveEffect() {
  return <span className="goalkeeper-technique-save-effect" aria-hidden="true">
    <i className="goalkeeper-technique-save-burst" />
    <svg viewBox="0 0 100 100" focusable="false">
      <path d="M45 87c-9-2-15-8-18-17L18 47c-2-5 4-8 7-4l9 14-17-37c-2-5 5-8 8-3l15 28-9-36c-1-5 6-7 8-2l9 34V7c0-5 7-5 8 0v34l9-32c2-5 9-3 8 2l-9 35 14-25c3-5 10-1 7 4L68 56l10-12c4-4 10 1 6 6L67 70c-5 12-12 18-22 17Z" />
      <path className="goalkeeper-technique-save-palm" d="M34 59c8-7 22-9 33-2l-3 14c-8 8-21 9-30 1Z" />
    </svg>
    <b>!</b>
  </span>;
}

function FieldTechniqueEffect({ type }: { type: 'shot' | 'block' | 'dribble' }) {
  if (type === 'shot') return <span className="field-technique-effect field-technique-effect--shot" aria-hidden="true">
    <i /><i /><i /><b />
  </span>;
  if (type === 'block') return <span className="field-technique-effect field-technique-effect--block" aria-hidden="true">
    <i /><i /><svg viewBox="0 0 100 100" focusable="false"><path d="M50 6 88 28v44L50 94 12 72V28Z" /><path d="m31 48 13 13 26-27" /></svg>
  </span>;
  return <span className="field-technique-effect field-technique-effect--dribble" aria-hidden="true">
    <i /><i /><i /><b />
  </span>;
}

function FuryTimers({ states, teams }: { states: readonly { active: boolean; remainingSeconds: number }[]; teams: readonly [string, string] }) {
  const activeTeams = ([0, 1] as const).filter((side) => states[side]?.active);
  if (!activeTeams.length) return null;
  return <div className="fury-timers" aria-live="polite" aria-label="Tiempo restante de las Fases de Furor">
    {activeTeams.map((side) => <div className={`fury-timer team-${side}`} key={side} title={`${teams[side]} · Fase de Furor`}>
      <Zap size={13} aria-hidden="true" />
      <span>FUROR</span>
      <b>{formatClock(Math.ceil(states[side].remainingSeconds))}</b>
    </div>)}
  </div>;
}

function PitchBallOverlay({ result, matchSecond, presentationFrame, action }: { result: MatchResult; matchSecond: number; presentationFrame?: WorldFrame; action?: MatchEvent }) {
  const frame = presentationFrame ?? frameAt(result.frames, matchSecond);
  const actionType = action?.type.toLowerCase() ?? '';
  const actorTeam = action ? frame.players.find((player) => player.id === action.actorId && player.team === action.team)?.team : undefined;
  return <span className={`pitch-ball ${actionType ? `ball-action-${actionType}` : ''} ${actorTeam === undefined ? '' : `ball-from-team-${actorTeam}`}`} style={{ left: `${frame.ball.x}%`, top: `${frame.ball.y}%`, '--ball-height': `${frame.ball.height * 5}px`, '--ball-shadow-scale': String(Math.max(.45, 1 - frame.ball.height * .09)) } as CSSProperties} aria-label="Balón" key={action ? `${action.second}-${action.type}` : 'idle'}>
    <img src="/football-ball.png" alt="" aria-hidden="true" draggable={false} />
  </span>;
}

function CommentaryPopup({ commentator, event, matchSeed }: { commentator?: MatchOfficial; event?: MatchEvent; matchSeed: number }) {
  const [state, setState] = useState<'hidden' | 'entering' | 'visible' | 'leaving'>('hidden');
  const eventKey = event ? `${matchSeed}-${event.second}-${event.type}-${event.actorId}` : '';
  useEffect(() => {
    if (!event) { setState('hidden'); return; }
    setState('entering');
    const visibleTimer = window.setTimeout(() => setState('visible'), 240);
    const leavingTimer = window.setTimeout(() => setState('leaving'), 4300);
    const hiddenTimer = window.setTimeout(() => setState('hidden'), 4720);
    return () => { window.clearTimeout(visibleTimer); window.clearTimeout(leavingTimer); window.clearTimeout(hiddenTimer); };
  }, [eventKey]);

  if (!event || !commentator || state === 'hidden') return null;
  const furyStart = event.type === 'FURY' && event.message.includes('entra en');
  return <aside className={`commentary-popup commentary-popup--${state} ${event.type === 'GOAL' ? 'commentary-popup--goal' : ''} ${furyStart ? 'commentary-popup--fury' : ''} ${event.type === 'SUBSTITUTION' ? 'commentary-popup--substitution' : ''}`} style={{ '--commentator-color': commentator.color } as CSSProperties}>
    <span className="commentator-avatar" title={`${commentator.role}: ${commentator.player.name}`}><PlayerAvatar player={commentator.player} size="md" /></span>
    <div className="commentary-bubble"><small>{commentator.player.name.toUpperCase()} · {commentator.role.toUpperCase()}</small><p><CommentaryMessage event={event} commentator={commentator} /></p></div>
  </aside>;
}

function GoalCelebration({ event, active }: { event?: MatchEvent; active: boolean }) {
  if (!event || !active) return null;
  return <div className="goal-celebration" role="status" aria-live="assertive">
    <span className="goal-celebration-burst" aria-hidden="true" />
    <span className="goal-celebration-kicker">EL ESTADIO ESTALLA</span>
    <strong>¡GOOOL!</strong>
    <p><b>{event.actorAlias ?? playerAlias(event.actorId)}</b> manda el balón a la red</p>
    <span className="goal-celebration-sparks" aria-hidden="true"><i /><i /><i /><i /><i /><i /></span>
  </div>;
}

function KickoffRestart({ team }: { team: Team }) {
  return <div className="kickoff-restart" role="status" aria-live="polite"><Zap size={17} /><span>SAQUE DE CENTRO</span><b>{team.name}</b><small>El juego se reanuda</small></div>;
}

function EventMessage({ event }: { event: MatchEvent }) {
  return <NarrationText narration={matchLogNarration(event)} />;
}

function CommentaryMessage({ event, commentator }: { event: MatchEvent; commentator: MatchOfficial }) {
  return <NarrationText narration={commentatorNarration(event, commentator.player.id)} />;
}

function NarrationText({ narration }: { narration: MatchNarration }) {
  if (!narration.emphasis.length) return <>{narration.text}</>;
  const expression = new RegExp(`(${narration.emphasis.map(escapeRegExp).join('|')})`, 'g');
  return <>{narration.text.split(expression).filter(Boolean).map((part, index) => narration.emphasis.includes(part)
    ? <strong key={`${part}-${index}`}>{part}</strong>
    : <span key={`${part}-${index}`}>{part}</span>)}</>;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function PixiPitch({ result, matchSecond, presentationFrame, debugEnabled }: { result: MatchResult; matchSecond: number; presentationFrame?: WorldFrame; debugEnabled: boolean }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef({ result, matchSecond, presentationFrame, debugEnabled });
  stateRef.current = { result, matchSecond, presentationFrame, debugEnabled };

  useEffect(() => {
    if (!hostRef.current) return;
    const host = hostRef.current;
    let cancelled = false;
    let initialized = false;
    let app: Application | null = null;
    const initialize = async () => {
      app = new Application();
      await app.init({ resizeTo: host, backgroundAlpha: 0, antialias: true, resolution: Math.min(devicePixelRatio, 2), autoDensity: true });
      initialized = true;
      if (cancelled || !app) { app?.destroy(true, { children: true }); return; }
      host.appendChild(app.canvas);
      const field = new Graphics();
      const aiDebug = new Graphics();
      const trail = new Graphics();
      const ballShadow = new Graphics().ellipse(0, 0, 13, 5).fill({ color: 0x02080d, alpha: 0.48 });
      const ballTarget = { x: 0, y: 0 };
      const ballHistory: { x: number; y: number }[] = [];
      app.stage.addChild(field, aiDebug, trail, ballShadow);
      const drawField = () => {
        if (!app) return;
        const w = app.screen.width;
        const h = app.screen.height;
        const pad = Math.max(20, Math.min(w, h) * 0.045);
        const fw = w - pad * 2;
        const fh = h - pad * 2;
        field.clear();
        // Turf: base, alternating mowing stripes and a darker touchline band.
        const line = { color: 0xffffff, width: 3, alpha: 0.88 };
        field.roundRect(pad, pad, fw, fh, 10).fill(0x158a43);
        for (let index = 0; index < 14; index++) if (index % 2 === 0) field.rect(pad + fw / 14 * index, pad, fw / 14, fh).fill({ color: 0x1ba350, alpha: 0.7 });
        field.roundRect(pad, pad, fw, fh, 10).stroke({ color: 0x0b4a24, width: 10, alpha: 0.35 });
        field.roundRect(pad, pad, fw, fh, 10).stroke(line);
        // Halfway line, centre circle and spot.
        field.moveTo(w / 2, pad).lineTo(w / 2, h - pad).stroke(line);
        const centreRadius = Math.min(fw, fh) * 0.13;
        field.circle(w / 2, h / 2, centreRadius).stroke(line);
        field.circle(w / 2, h / 2, 4).fill(0xffffff);
        // Penalty and goal areas, penalty spots and the arcs outside the box.
        const boxW = fw * 0.15;
        const goalAreaW = fw * 0.055;
        const spotX = fw * 0.1;
        const arcRadius = Math.max(spotX * 0.6, fh * 0.13);
        const arcAngle = Math.acos(Math.min(1, (boxW - spotX) / arcRadius));
        for (const side of [0, 1]) {
          const sign = side === 0 ? 1 : -1;
          const edge = side === 0 ? pad : w - pad;
          field.rect(side === 0 ? edge : edge - boxW, h * 0.28, boxW, h * 0.44).stroke(line);
          field.rect(side === 0 ? edge : edge - goalAreaW, h * 0.39, goalAreaW, h * 0.22).stroke(line);
          field.circle(edge + sign * spotX, h / 2, 3.5).fill(0xffffff);
          const arcStart = side === 0 ? -arcAngle : Math.PI - arcAngle;
          const arcCentreX = edge + sign * spotX;
          field.moveTo(arcCentreX + Math.cos(arcStart) * arcRadius, h / 2 + Math.sin(arcStart) * arcRadius).arc(arcCentreX, h / 2, arcRadius, arcStart, arcStart + arcAngle * 2).stroke(line);
          // Goal frame with a net hint behind the line.
          const goalX = side === 0 ? edge - 9 : edge;
          field.rect(goalX, h * 0.42, 9, h * 0.16).fill({ color: 0xffffff, alpha: 0.12 }).stroke({ color: 0xffffff, width: 3, alpha: 0.95 });
        }
        // Corner arcs.
        const corner = 12;
        field.moveTo(pad + corner, pad).arc(pad, pad, corner, 0, Math.PI / 2).stroke(line);
        field.moveTo(w - pad, pad + corner).arc(w - pad, pad, corner, Math.PI / 2, Math.PI).stroke(line);
        field.moveTo(w - pad - corner, h - pad).arc(w - pad, h - pad, corner, Math.PI, Math.PI * 1.5).stroke(line);
        field.moveTo(pad, h - pad - corner).arc(pad, h - pad, corner, Math.PI * 1.5, Math.PI * 2).stroke(line);
      };
      drawField();
      const resizeObserver = new ResizeObserver(drawField);
      resizeObserver.observe(host);
      app.ticker.add(() => {
        if (!app) return;
        const current = stateRef.current;
        const frame = current.presentationFrame ?? frameAt(current.result.frames, current.matchSecond);
        const pad = Math.max(20, Math.min(app.screen.width, app.screen.height) * 0.045);
        const fw = app.screen.width - pad * 2;
        const fh = app.screen.height - pad * 2;
        const screenPoint = (point: { x: number; y: number }) => ({ x: pad + point.x / 100 * fw, y: pad + point.y / 100 * fh });
        aiDebug.clear();
        if (current.debugEnabled) {
          for (const player of frame.players) {
            const position = screenPoint(player);
            const anchor = screenPoint(player.anchorPosition);
            const ideal = screenPoint(player.idealTarget);
            const reachable = screenPoint(player.reachableTarget);
            aiDebug.moveTo(position.x, position.y).lineTo(anchor.x, anchor.y).stroke({ color: 0x63d7ff, width: 1, alpha: .55 });
            aiDebug.circle(anchor.x, anchor.y, 3).stroke({ color: 0x63d7ff, width: 1.2, alpha: .9 });
            aiDebug.moveTo(position.x, position.y).lineTo(ideal.x, ideal.y).stroke({ color: 0xffb15f, width: 1.4, alpha: .75 });
            aiDebug.circle(ideal.x, ideal.y, 3).fill({ color: 0xffb15f, alpha: .9 });
            aiDebug.moveTo(position.x, position.y).lineTo(reachable.x, reachable.y).stroke({ color: 0x68e69d, width: 2, alpha: .85 });
            aiDebug.circle(reachable.x, reachable.y, 2.5).fill({ color: 0x68e69d, alpha: .95 });
          }
          for (const decision of frame.debug.decisions) {
            const actor = frame.players.find((player) => player.id === decision.actorId && player.team === decision.team);
            if (!actor) continue;
            const position = screenPoint(decision.position);
            const perceptionRadius = Math.max(8, decision.nearestOpponentDistance / 100 * fw);
            aiDebug.circle(position.x, position.y, perceptionRadius).stroke({ color: decision.team === 0 ? 0x62cffa : 0xff8597, width: 1.5, alpha: .55 });
            const nearest = decision.nearestOpponentId
              ? frame.players.find((player) => player.id === decision.nearestOpponentId && player.team !== decision.team)
              : undefined;
            if (nearest) {
              const opponent = screenPoint(nearest);
              aiDebug.moveTo(position.x, position.y).lineTo(opponent.x, opponent.y).stroke({ color: 0xffef78, width: 1.2, alpha: .7 });
            }
          }
        }
        ballTarget.x = pad + frame.ball.x / 100 * (app.screen.width - pad * 2);
        ballTarget.y = pad + frame.ball.y / 100 * (app.screen.height - pad * 2);
        const height = frame.ball.height ?? 0;
        ballShadow.x += (ballTarget.x - ballShadow.x) * 0.25;
        ballShadow.y += (ballTarget.y + 8 + height * 1.2 - ballShadow.y) * 0.25;
        ballShadow.scale.set(Math.max(.45, 1 - height * .09));
        ballShadow.alpha = Math.max(.15, .48 - height * .055);
        ballHistory.push({ x: ballTarget.x, y: ballTarget.y });
        if (ballHistory.length > 8) ballHistory.shift();
        trail.clear();
        ballHistory.forEach((point, index) => trail.circle(point.x, point.y, 2 + index * 0.24).fill({ color: 0x8ee7ff, alpha: index / 35 }));
      });
      (host as HTMLDivElement & { cleanup?: () => void }).cleanup = () => resizeObserver.disconnect();
    };
    void initialize();
    return () => {
      cancelled = true;
      (host as HTMLDivElement & { cleanup?: () => void }).cleanup?.();
      if (initialized) app?.destroy(true, { children: true });
    };
  }, [result.seed]);
  return <div ref={hostRef} className="pixi-pitch" />;
}

function AIDebugOverlay({ result, matchSecond, presentationFrame }: { result: MatchResult; matchSecond: number; presentationFrame?: WorldFrame }) {
  const frame = presentationFrame ?? frameAt(result.frames, matchSecond);
  return <div className="ai-debug-overlay" aria-label="Depuración visual de la inteligencia artificial">
    <div className="ai-debug-intents">
      {frame.players.map((player, index) => <span
        className={`team-${player.team}`}
        style={{ left: `${player.x}%`, top: `${player.y}%` }}
        key={`${player.team}-${player.id}-${index}`}
      >{player.intentReason}</span>)}
    </div>
    <section className="ai-debug-panel">
      <header><b>IA 2.3</b><span>{frame.teamPhases[0]} · {frame.teamPhases[1]}</span></header>
      <div className="ai-debug-legend"><i /> anchor <i /> ideal <i /> alcanzable</div>
      {frame.debug.decisions.length ? frame.debug.decisions.map((decision, index) => <article key={`${decision.team}-${decision.actorId}-${index}`}>
        <strong>{playerAlias(decision.actorId)} · {decision.chosenAction}</strong>
        <small>t−{Math.max(0, frame.second - decision.second).toFixed(1)}s · presión {decision.pressure.toFixed(2)} · espacio {decision.localSpace.toFixed(2)} · rival {decision.nearestOpponentDistance.toFixed(1)}</small>
        <ol>{decision.scores.map((score, scoreIndex) => <li className={score.chosen ? 'chosen' : ''} key={`${score.type}-${scoreIndex}`}><span>{score.passType ? `${score.type} · ${score.passType}` : score.type}</span><b>{score.score.toFixed(1)}</b><em>{(score.probability * 100).toFixed(0)}%</em></li>)}</ol>
      </article>) : <p>Esperando una decisión…</p>}
    </section>
  </div>;
}

function frameAt(frames: WorldFrame[], matchSecond: number): WorldFrame {
  let lower = 0;
  let upper = frames.length - 1;
  while (lower < upper) {
    const middle = Math.ceil((lower + upper) / 2);
    if (frames[middle].second <= matchSecond) lower = middle;
    else upper = middle - 1;
  }
  const current = frames[lower];
  const next = frames[Math.min(frames.length - 1, lower + 1)];
  const amount = next.second === current.second ? 1 : Math.max(0, Math.min(1, (matchSecond - current.second) / (next.second - current.second)));
  return interpolateFrame(current, next, amount);
}

function interpolateFrame(a: WorldFrame, b: WorldFrame, amount: number): WorldFrame {
  const players = a.players.map((player, index) => ({ ...player, x: lerp(player.x, b.players[index]?.x ?? player.x, amount), y: lerp(player.y, b.players[index]?.y ?? player.y, amount) }));
  return { ...a, players, ball: { ...a.ball, x: lerp(a.ball.x, b.ball.x, amount), y: lerp(a.ball.y, b.ball.y, amount), height: lerp(a.ball.height, b.ball.height, amount) } };
}

function LiveStat({ label, home, away, suffix = '' }: { label: string; home: number; away: number; suffix?: string }) {
  const total = home + away || 1;
  return <div className="live-stat"><span><b>{home}{suffix}</b><small>{label}</small><b>{away}{suffix}</b></span><div><i style={{ width: `${home / total * 100}%` }} /><em style={{ width: `${away / total * 100}%` }} /></div></div>;
}

const lerp = (a: number, b: number, amount: number) => a + (b - a) * amount;
const formatMinute = (seconds: number) => `${Math.min(90, Math.floor(seconds / 60) + 1)}'`;
const formatClock = (seconds: number) => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
const playerAlias = (id: string) => playerById.get(id)?.metadata?.nickname?.trim() || playerById.get(id)?.name.split(' ')[0] || '?';
const playerAliasFromName = (name: string) => [...playerById.values()].find((player) => player.name === name)?.metadata?.nickname?.trim() || name.split(' ')[0] || '?';
const playerAverage = (player: Player) => playerRating(player);

function officialForSeed(seed: number): MatchOfficial | undefined {
  const official = MATCH_OFFICIALS[Math.abs(seed) % MATCH_OFFICIALS.length];
  const player = playerById.get(official.playerId);
  return player ? { ...official, player } : undefined;
}

function actionAt(events: MatchEvent[], matchSecond: number): MatchEvent | undefined {
  const event = events.filter((candidate) => candidate.second <= matchSecond).at(-1);
  return event && matchSecond - event.second <= ACTION_DISPLAY_SECONDS ? event : undefined;
}

function nextMatchPreview(save: SaveGame, completedMatchday: number): { season: number; matchday: number; clubIsHome: boolean; opponent: RivalTeam } | null {
  const currentGroup = findClubGroup(createLeagueGroups(save.leagueSeed, save.clubName));
  const currentSchedule = createDoubleRoundRobinSchedule(currentGroup.teams.map((team) => team.id));
  const startsNewSeason = completedMatchday >= currentSchedule.length;
  const season = startsNewSeason ? save.season + 1 : save.season;
  const leagueSeed = startsNewSeason
    ? nextLeagueSeed(save.leagueSeed, season, (save.nextSeed + 104729) ^ 0x9E3779B)
    : save.leagueSeed;
  const group = startsNewSeason ? findClubGroup(createLeagueGroups(leagueSeed, save.clubName)) : currentGroup;
  // `completedMatchday` is human-readable (1-based), which is precisely the
  // zero-based index of the following fixture. A new season restarts at zero.
  const scheduled = findClubFixture(group, startsNewSeason ? 0 : completedMatchday);
  if (!scheduled) return null;
  const clubIsHome = scheduled.fixture.homeId === 'team_aurora';
  const opponentId = clubIsHome ? scheduled.fixture.awayId : scheduled.fixture.homeId;
  const opponent = group.teams.find((team) => team.id === opponentId);
  return opponent ? { season, matchday: scheduled.matchday.number, clubIsHome, opponent } : null;
}

/** Computes the table with the current club fixture included, before the
 * season rollover clears results. It is used only for the end-of-season
 * celebration and mirrors the result registration path in gameState. */
function clubWinsLeagueOnFinalMatch(save: SaveGame, match: ScheduledMatch, result: MatchResult): boolean {
  const group = findClubGroup(createLeagueGroups(save.leagueSeed, save.clubName));
  const schedule = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id));
  if (match.matchday !== schedule.length) return false;
  const score: [number, number] = [result.homeScore, result.awayScore];
  const completedMatchday = completeLeagueMatchday(group, save.leagueMatchday, score, result.seed);
  const previous = save.leagueResults.filter((entry) => entry.matchday !== match.matchday);
  return createStandings(group, [...previous, ...completedMatchday], save.leaguePointAdjustments)[0]?.team.id === 'team_aurora';
}

function createScheduledMatch(save: SaveGame): ScheduledMatch {
  const calendarFixture = nextClubFixture(save);
  const group = findClubGroup(createLeagueGroups(save.leagueSeed, save.clubName));
  const scheduled = findClubFixture(group, save.leagueMatchday);
  const specialAffinity = calendarFixture?.competition === 'affinity_games';
  const allLeagueTeams = new Map(createLeagueGroups(save.leagueSeed, save.clubName).flatMap((entry) => entry.teams).map((team) => [team.id, team]));
  const allTeams = specialAffinity
    ? new Map(affinityTeams(save).map((team) => [team.id, team]))
    : calendarFixture?.competition === 'champions' || calendarFixture?.competition === 'kings_cup'
      ? allLeagueTeams
      : new Map(group.teams.map((team) => [team.id, team]));
  const fixture = calendarFixture ?? (scheduled ? { homeId: scheduled.fixture.homeId, awayId: scheduled.fixture.awayId, competition: 'league' as const, label: group.name, matchday: scheduled.matchday.number, date: leagueDate(save.season, scheduled.matchday.number), round: undefined } : null);
  const clubId = specialAffinity ? AFFINITY_USER_TEAM_ID : 'team_aurora';
  const opponentId = fixture?.homeId === clubId ? fixture.awayId : fixture?.homeId;
  const opponent = opponentId ? allTeams.get(opponentId) : undefined;
  const normalClubPlayers = save.lineup.flatMap((id) => {
    const player = resolveClubPlayer(save, id);
    return player ? [player] : [];
  });
  const affinityManager = specialAffinity ? allTeams.get(AFFINITY_USER_TEAM_ID) : undefined;
  const affinitySquad = affinityManager?.lineupPlayerIds
    ?.flatMap((id) => affinityManager.players.find((player) => player.id === id) ?? []) ?? [];
  const affinityBench = affinityManager?.benchPlayerIds
    ?.flatMap((id) => affinityManager.players.find((player) => player.id === id) ?? []) ?? [];
  const clubPlayers = specialAffinity ? affinitySquad : normalClubPlayers;
  const clubBench = specialAffinity ? affinityBench : save.benchPlayerIds.flatMap((id) => {
    const player = resolveClubPlayer(save, id);
    return player ? [player] : [];
  });
  const club: Team = {
    ...teams[0],
    id: clubId,
    name: specialAffinity ? (allTeams.get(AFFINITY_USER_TEAM_ID)?.name ?? `Selección ${save.affinityGames?.element ?? ''}`) : save.clubName,
    formation_id: specialAffinity ? save.affinityGames?.formationId ?? 'formation_4_4_2' : save.formationId,
    formation: specialAffinity
      ? save.customFormations.find((item) => item.id === save.affinityGames?.formationId)
      : save.customFormations.find((item) => item.id === save.formationId),
    lineup: clubPlayers.map((player) => player.id),
    playerOverrides: Object.fromEntries(clubPlayers.map((player) => [player.id, player])),
    tacticalRoleOverrides: specialAffinity ? save.affinityGames?.tacticalRoleOverrides : save.tacticalRoleOverrides,
    tactics: { ...teams[0].tactics, ...save.tactics },
  };
  const rival = opponent ? toMatchTeam(opponent, save) : teams[1];
  const clubIsHome = fixture?.homeId === clubId;
  return {
    groupName: group.name,
    matchday: fixture?.matchday ?? 1,
    date: fixture?.date ?? save.careerDate,
    championsRound: fixture?.round,
    competition: fixture?.competition ?? 'league',
    competitionLabel: fixture?.label ?? group.name,
    clubIsHome: Boolean(clubIsHome),
    clubTemporary: Boolean(specialAffinity),
    clubDisplayName: club.name,
    clubPlayers,
    clubBench,
    home: clubIsHome ? club : rival,
    away: clubIsHome ? rival : club,
    seed: save.nextSeed,
  };
}

function toMatchTeam(team: RivalTeam, save: SaveGame): Team {
  const selected = selectRivalLineup(team);
  if (!selected) throw new Error(`El rival ${team.name} no dispone de un once válido.`);
  const lineup = selected.map((player) => player.id);
  return {
    id: team.id,
    name: team.name,
    formation_id: team.formation_id ?? 'formation_4_4_2',
    lineup,
    playerOverrides: Object.fromEntries(lineup.flatMap((id) => {
      const player = resolveWorldPlayer(save, id);
      return player ? [[id, player]] : [];
    })),
    tactics: { mentality: .52, pressing: .54, tempo: .52, width: .5, defensiveLine: .5, risk: .5, techniqueAggression: .5 },
  };
}
